import type { RecordsGatewayPort } from "@/application/records/ports/RecordsGatewayPort";
import { RetryService } from "@/application/common/services/RetryService";
import { MetricsService } from "@/application/common/services/MetricsService";
import { ErrorHandlerService } from "@/application/common/services/ErrorHandlerService";
import {
  EventBus,
  RECORD_LINKED_EVENT,
  RECORD_LINK_FAILED_EVENT,
} from "@/application/common/events/EventBus";
import { assertNoDemoReportMutations } from "./demoReportMutationGuard";

export interface AssignRecordToProjectInputDto {
  recordIds: string[];
  projectId: string;
  userId?: string;
}

/**
 * Use case: assigns records to a project (sets project_id to specified value)
 * Clean Architecture - Application Layer
 */
export class AssignRecordToProjectUseCase {
  constructor(
    private readonly gateway: RecordsGatewayPort,
    private readonly retryService: RetryService,
    private readonly metricsService: MetricsService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly eventBus: EventBus
  ) {}

  async execute(input: AssignRecordToProjectInputDto): Promise<void> {
    const { recordIds, projectId, userId } = input;
    if (!recordIds.length) {
      return;
    }

    const startTime = Date.now();
    const context = {
      operation: "assignRecordToProject",
      userId,
      recordIds,
      projectId,
    };

    try {
      await this.retryService.executeWithRetry(async () => {
        const request = {
          change_names: [],
          for_archive: [],
          for_delete: [],
          delete_link: [],
          assign_link: recordIds.map((recordId) => ({
            record_id: recordId,
            project_id: projectId,
          })),
        };
        assertNoDemoReportMutations(request);
        await this.gateway.updateRecords(request);
      }, "assignRecordToProject");

      const duration = Date.now() - startTime;
      this.metricsService.recordMetric({
        operation: "assignRecordToProject",
        duration,
        success: true,
        metadata: { recordCount: recordIds.length, projectId },
      });

      // Публикуем событие успеха
      await this.eventBus.publish({
        type: RECORD_LINKED_EVENT,
        payload: { recordIds, projectId },
        timestamp: Date.now(),
        userId,
      });
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorObj =
        error instanceof Error ? error : new Error(String(error));

      this.metricsService.recordMetric({
        operation: "assignRecordToProject",
        duration,
        success: false,
        error: errorObj.message,
        metadata: { recordCount: recordIds.length, projectId },
      });

      // Публикуем событие ошибки
      await this.eventBus.publish({
        type: RECORD_LINK_FAILED_EVENT,
        payload: { recordIds, projectId, error: errorObj.message },
        timestamp: Date.now(),
        userId,
      });

      const userMessage = this.errorHandler.handleError(errorObj, context);
      throw new Error(userMessage);
    }
  }
}
